import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { 
  Download, Upload, CheckCircle2, Shield, Info, ArrowRight, Zap, 
  Cpu, Terminal, Activity, AlertTriangle, Layers, BookOpen, Clock, 
  HelpCircle, Monitor, Play, RefreshCw, AppWindow, Eye, Lock, CheckCircle,
  MessageSquare, HardDrive, Smartphone, Compass
} from 'lucide-react';
import styles from './GuidePage.module.css';

export function GuidePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'recommendations' | 'specs' | 'battery' | 'performance' | 'startup'>('recommendations');
  const [expandedRec, setExpandedRec] = useState<string | null>('disc-space');
  const mockupRef = useRef<HTMLDivElement>(null);

  const scrollToMockup = () => {
    mockupRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleStartScan = () => {
    navigate('/diagnostic');
  };

  const handleUpgradePro = () => {
    navigate('/pricing');
  };

  return (
    <div className="app">
      <Header />
      
      <main className={styles.guidePage}>
        <div className="wrap">
          {/* Header Section: What the guide is for */}
          <section className={styles.intro + " fade-up"}>
            <div className="section-label">User Guide</div>
            <h1>Simple. Safe. <span>Plain-English PC Diagnostics.</span></h1>
            <p>
              Find out exactly what is slowing down your Windows PC. Run our lightweight, read-only scanner 
              and get professional-grade results explained in simple terms.
            </p>
          </section>

          {/* Top CTA Row */}
          <div className={styles.ctaRow}>
            <button onClick={handleStartScan} className="btn btn-primary">
              Start Free PC Check <Zap size={16} />
            </button>
            <button onClick={scrollToMockup} className="btn btn-secondary">
              View Pro Example Report <ArrowRight size={16} />
            </button>
          </div>

          {/* Section 1: Trust Indicators */}
          <section className={styles.trustSection}>
            <div className={styles.trustGrid}>
              <div className={styles.trustCard}>
                <div className={styles.trustIcon}><Terminal size={24} /></div>
                <h4>No Installation Required</h4>
                <p>Runs as a lightweight, standalone tool. Leaves zero residue and takes no space on your drive.</p>
              </div>
              <div className={styles.trustCard}>
                <div className={styles.trustIcon}><Eye size={24} /></div>
                <h4>Read-Only Scan</h4>
                <p>We only read technical parameters. The script is completely open-source and cannot modify settings.</p>
              </div>
              <div className={styles.trustCard}>
                <div className={styles.trustIcon}><Shield size={24} /></div>
                <h4>Your Files Are Safe</h4>
                <p>PCCheckr never touches personal documents, photos, browsing histories, or account credentials.</p>
              </div>
              <div className={styles.trustCard}>
                <div className={styles.trustIcon}><Lock size={24} /></div>
                <h4>Secure Encrypted Upload</h4>
                <p>All data uploads are protected with modern, industry-standard SSL encryption.</p>
              </div>
            </div>
          </section>

          {/* Section 2: 4-Step Guide */}
          <section className={styles.stepsSection}>
            <div className="section-header">
              <h2>4 Easy Steps to Get Your PC Report</h2>
              <p className="section-desc">You do not need to be a tech wizard. Just follow these simple steps to run the diagnostic scan.</p>
            </div>

            <div className={styles.stepsGrid}>
              {/* Step 1 */}
              <div className={styles.stepCard}>
                <span className={styles.stepNumber}>Step 1</span>
                <h3>Download the Scanner</h3>
                <p>
                  Get the PCCheckr Scanner. It is a tiny, read-only script (`.bat`) 
                  pre-configured to run on all modern versions of Windows.
                </p>
                <div className={styles.stepBadge}>Safe Download</div>
              </div>

              {/* Step 2 */}
              <div className={styles.stepCard}>
                <span className={styles.stepNumber}>Step 2</span>
                <h3>Double-Click to Run</h3>
                <p>
                  Launch the script. In about 10 seconds, it will collect diagnostic metadata 
                  (like startup loads and storage health) and place a `.pcc` file on your desktop.
                </p>
                <div className={styles.stepBadge}>Takes 10 Seconds</div>
              </div>

              {/* Step 3 */}
              <div className={styles.stepCard}>
                <span className={styles.stepNumber}>Step 3</span>
                <h3>Upload Your .pcc File</h3>
                <p>
                  Drag and drop the `.pcc` file into your dashboard. Our engine processes the data 
                  privately without making any changes to your PC.
                </p>
                <div className={styles.stepBadge}>Encrypted Upload</div>
              </div>

              {/* Step 4 */}
              <div className={styles.stepCard}>
                <span className={styles.stepNumber}>Step 4</span>
                <h3>Read Plain-English Results</h3>
                <p>
                  Instantly see your overall health score, along with prioritized recommendations 
                  explaining what is slowing you down and how to resolve it safely.
                </p>
                <div className={styles.stepBadge}>No Technical Jargon</div>
              </div>
            </div>
          </section>

          {/* Section 3: What Happens After Upload? */}
          <section className={styles.afterUploadSection}>
            <div className="section-header">
              <h2>What Happens After You Upload?</h2>
              <p className="section-desc">Once you submit your scan file, PCCheckr's automated diagnostics go to work.</p>
            </div>
            
            <div className={styles.flowGrid}>
              <div className={styles.flowItem}>
                <div className={styles.flowNumber}>A</div>
                <h3>Deep AI Analysis</h3>
                <p>Our secure engine parses the system data to look for patterns of wear, misconfigured settings, and hardware limits.</p>
              </div>
              <div className={styles.flowItem}>
                <div className={styles.flowNumber}>B</div>
                <h3>Telemetry Checks</h3>
                <p>PCCheckr checks startup apps, RAM usage, battery degradation, SSD storage levels, and OS stability signals.</p>
              </div>
              <div className={styles.flowItem}>
                <div className={styles.flowNumber}>C</div>
                <h3>Prioritized Action List</h3>
                <p>Instead of cryptic errors, you receive simple, step-by-step guidance on how to fix issues yourself safely.</p>
              </div>
            </div>
          </section>

          {/* Section 4: Sample Pro Findings */}
          <section className={styles.sampleFindingsSection}>
            <div className="section-header">
              <h2>Sample Pro Findings</h2>
              <p className="section-desc">Here are some of the common bottlenecks our 80-point Pro diagnostic check exposes:</p>
            </div>

            <div className={styles.findingsGrid}>
              <div className={styles.findingCard}>
                <Play size={20} className={styles.findingIcon} style={{ color: 'var(--amber)' }} />
                <h4>Too Many Startup Apps</h4>
                <p>Identifies silent background apps (like Discord, Steam, or Spotify) that open on login and waste CPU cycles.</p>
              </div>
              <div className={styles.findingCard}>
                <HardDrive size={20} className={styles.findingIcon} style={{ color: 'var(--red)' }} />
                <h4>Low SSD Free Space</h4>
                <p>Exposes when your primary drive is running out of space, which restricts Windows virtual memory usage and slows speeds.</p>
              </div>
              <div className={styles.findingCard}>
                <Smartphone size={20} className={styles.findingIcon} style={{ color: 'var(--green)' }} />
                <h4>Battery Wear Detected</h4>
                <p>Reveals design capacity vs. actual charge capacity to show if your battery needs replacement.</p>
              </div>
              <div className={styles.findingCard}>
                <Activity size={20} className={styles.findingIcon} style={{ color: 'var(--accent)' }} />
                <h4>High RAM Overhead</h4>
                <p>Tracks background services that drain available memory, causing web browsers or games to stutter.</p>
              </div>
            </div>
          </section>

          {/* Section 5: Social Proof / Testimonials */}
          <section className={styles.testimonialSection}>
            <div className="section-header">
              <h2>Loved by PC Owners</h2>
              <p className="section-desc">See what early users have to say about diagnosing their machines with PCCheckr.</p>
            </div>
            
            <div className={styles.testimonialGrid}>
              <div className={styles.testimonialCard}>
                <div className={styles.stars}>★★★★★</div>
                <p>“PCCheckr helped identify startup apps slowing my laptop. I disabled them and my boot time cut in half.”</p>
                <div className={styles.author}>— Early Access User</div>
              </div>
              <div className={styles.testimonialCard}>
                <div className={styles.stars}>★★★★★</div>
                <p>“I loved that I didn't have to install any bulky diagnostic software. Just a simple script, and a beautiful report.”</p>
                <div className={styles.author}>— Freelancer</div>
              </div>
              <div className={styles.testimonialCard}>
                <div className={styles.stars}>★★★★★</div>
                <p>“It translated confusing Windows errors into actual steps I could take. Clean, fast, and simple.”</p>
                <div className={styles.author}>— Non-Technical User</div>
              </div>
            </div>
          </section>

          {/* Section 6: Interactive Pro Report Mockup Showcase */}
          <section ref={mockupRef} className={styles.showcaseSection}>
            <div className={styles.showcaseHeader}>
              <div className="section-label">Interactive Preview</div>
              <h2>Inside your Pro Diagnostic Report</h2>
              <p>
                Explore a demonstration of our premium dashboard. Use the tabs inside the mockup below 
                to see how we present your system configuration and performance recommendations.
              </p>
            </div>

            {/* Dashboard Mockup */}
            <div className={styles.mockReport}>
              <div className={styles.mockHeader}>
                <div className={styles.mockTitle}>
                  <h4>PCCheckr Pro Diagnostics</h4>
                  <span className={styles.proBadge}>PRO ACTIVE</span>
                </div>
                <div className={styles.mockMeta}>
                  <span>Report ID: PCC-DEMO-9284</span>
                  <span>Scanned: Today</span>
                </div>
              </div>

              {/* Grid: Sidebar (Score + specs) | Main display */}
              <div className={styles.mockGrid}>
                
                {/* Sidebar */}
                <div className={styles.sidebarColumn}>
                  {/* Score Card */}
                  <div className={styles.scoreCard}>
                    <div className={styles.scoreVisual}>
                      <svg className={styles.scoreRing} viewBox="0 0 100 100">
                        <circle className={styles.ringBg} cx="50" cy="50" r="45" />
                        <circle className={styles.ringFill} cx="50" cy="50" r="45" />
                      </svg>
                      <div className={styles.scoreText}>
                        <span className={styles.scoreNumber}>92</span>
                        <span className={styles.scoreLabel}>Health Score</span>
                      </div>
                    </div>
                    <div className={styles.scoreStatus}>
                      <h3>Excellent Health</h3>
                      <p>Your system is running efficiently but has a few minor optimizations available.</p>
                      <div className={styles.countsGrid}>
                        <div className={styles.countBadge + " " + styles.pass}>
                          <strong>74</strong>
                          <span>Pass</span>
                        </div>
                        <div className={styles.countBadge + " " + styles.warning}>
                          <strong>5</strong>
                          <span>Warn</span>
                        </div>
                        <div className={styles.countBadge + " " + styles.critical}>
                          <strong>1</strong>
                          <span>Crit</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Specs Snapshot */}
                  <div className={styles.specsCard}>
                    <div className={styles.specsHeader}>
                      <Monitor size={14} />
                      <h4>System Snapshot</h4>
                    </div>
                    <div className={styles.specsList}>
                      <div className={styles.specsItem}>
                        <span>OS</span>
                        <strong>Windows 11 Pro</strong>
                      </div>
                      <div className={styles.specsItem}>
                        <span>Manufacturer</span>
                        <strong>ASUSTeK COMPUTER</strong>
                      </div>
                      <div className={styles.specsItem}>
                        <span>Model</span>
                        <strong>ROG Zephyrus G14</strong>
                      </div>
                      <div className={styles.specsItem}>
                        <span>RAM</span>
                        <strong>16.0 GB Installed</strong>
                      </div>
                      <div className={styles.specsItem}>
                        <span>Uptime</span>
                        <strong>2.4 Days</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content Area */}
                <div className={styles.mainReportArea}>
                  
                  {/* Mock Tabs Navigation */}
                  <div className={styles.mockTabs}>
                    <button 
                      onClick={() => setActiveTab('recommendations')} 
                      className={`${styles.tabButton} ${activeTab === 'recommendations' ? styles.activeTab : ''}`}
                    >
                      <Info size={14} /> Priority Fixes
                    </button>
                    <button 
                      onClick={() => setActiveTab('specs')} 
                      className={`${styles.tabButton} ${activeTab === 'specs' ? styles.activeTab : ''}`}
                    >
                      <Layers size={14} /> System Specs
                    </button>
                    <button 
                      onClick={() => setActiveTab('battery')} 
                      className={`${styles.tabButton} ${activeTab === 'battery' ? styles.activeTab : ''}`}
                    >
                      <Zap size={14} /> Battery Health
                    </button>
                    <button 
                      onClick={() => setActiveTab('performance')} 
                      className={`${styles.tabButton} ${activeTab === 'performance' ? styles.activeTab : ''}`}
                    >
                      <Activity size={14} /> CPU / RAM Load
                    </button>
                    <button 
                      onClick={() => setActiveTab('startup')} 
                      className={`${styles.tabButton} ${activeTab === 'startup' ? styles.activeTab : ''}`}
                    >
                      <Play size={14} /> Startup Apps
                    </button>
                  </div>

                  {/* Tab Contents */}
                  
                  {/* TAB 1: RECOMMENDATIONS */}
                  {activeTab === 'recommendations' && (
                    <div className={styles.recList}>
                      {/* Critical item */}
                      <div 
                        className={styles.recItem}
                        onClick={() => setExpandedRec(expandedRec === 'disc-space' ? null : 'disc-space')}
                      >
                        <div className={styles.recTop}>
                          <div className={styles.recTitleWrap}>
                            <AlertTriangle size={18} color="var(--red)" />
                            <h4>Low Free Space on primary C: Drive</h4>
                          </div>
                          <span className={styles.severityTag + " " + styles.critical}>Critical</span>
                        </div>
                        
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-1)', margin: 0 }}>
                          Only 8.4 GB free of 256 GB on your primary SSD disk. Solid state drives slow down dramatically when below 10% capacity.
                        </p>

                        {expandedRec === 'disc-space' && (
                          <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
                            <div className={styles.recExplanation}>
                              <strong>Why it matters:</strong> Windows uses free drive space as "virtual memory" (pagefile). When free space is extremely low, applications can lag, lock up, or crash due to memory allocation failures.
                            </div>
                            <div className={styles.recTechnical}>
                              SYSTEM LOG EVIDENCE: DriveID=C: | Type=Local SSD | Size=256,160MB | FreeSpace=8,420MB | FreePercent=3.28%
                            </div>
                            <div className={styles.remediationRow}>
                              <div className={styles.remediationBox}>
                                <h5>Recommended Action</h5>
                                <p>Open Windows Storage Sense or run Disk Cleanup (`cleanmgr.exe`). Remove temporary system files and uninstall large, unused programs.</p>
                              </div>
                              <div className={styles.avoidBox}>
                                <h5>What to Avoid</h5>
                                <p>Do not use automatic "registry cleaners" or downloading third-party "cleaners" which often install bloatware or delete needed config files.</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Warning item */}
                      <div 
                        className={styles.recItem}
                        onClick={() => setExpandedRec(expandedRec === 'startup' ? null : 'startup')}
                      >
                        <div className={styles.recTop}>
                          <div className={styles.recTitleWrap}>
                            <Info size={18} color="var(--amber)" />
                            <h4>High Number of Startup Programs Active</h4>
                          </div>
                          <span className={styles.severityTag + " " + styles.warning}>Warning</span>
                        </div>

                        <p style={{ fontSize: '0.85rem', color: 'var(--text-1)', margin: 0 }}>
                          23 programs are launching automatically when you sign in to Windows. This increases boot times and active memory consumption.
                        </p>

                        {expandedRec === 'startup' && (
                          <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
                            <div className={styles.recExplanation}>
                              <strong>Why it matters:</strong> Every application starting up automatically consumes system RAM and CPU clock cycles in the background, even if you never open or use them.
                            </div>
                            <div className={styles.recTechnical}>
                              SYSTEM LOG EVIDENCE: StartupProgramsCount=23 | MemoryLoadAtBoot=71% | CPUIdleOverhead=9%
                            </div>
                            <div className={styles.remediationRow}>
                              <div className={styles.remediationBox}>
                                <h5>Recommended Action</h5>
                                <p>Open Windows Task Manager (Ctrl+Shift+Esc), navigate to the "Startup apps" tab, and disable unnecessary non-system programs.</p>
                              </div>
                              <div className={styles.avoidBox}>
                                <h5>What to Avoid</h5>
                                <p>Never disable drivers or core hardware tools (e.g. Realtek Audio, NVIDIA Control Panel, Windows Security Center components).</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* TAB 2: SYSTEM SPECS */}
                  {activeTab === 'specs' && (
                    <div className={styles.specsCard} style={{ marginTop: 0 }}>
                      <div className={styles.specsHeader}>
                        <Cpu size={16} />
                        <h3>Detailed Hardware Configuration</h3>
                      </div>
                      <div className={styles.specsList}>
                        <div className={styles.specsItem}>
                          <span>Processor (CPU)</span>
                          <strong>AMD Ryzen 9 5900HS (8 Cores, 16 Threads @ 3.0GHz)</strong>
                        </div>
                        <div className={styles.specsItem}>
                          <span>Physical Memory (RAM)</span>
                          <strong>16.0 GB DDR4 @ 3200MHz</strong>
                        </div>
                        <div className={styles.specsItem}>
                          <span>Primary Drive</span>
                          <strong>NVMe WD Blue SN570 SSD (256 GB)</strong>
                        </div>
                        <div className={styles.specsItem}>
                          <span>Graphics Adapter</span>
                          <strong>NVIDIA GeForce RTX 3060 Laptop GPU (6GB VRAM)</strong>
                        </div>
                        <div className={styles.specsItem}>
                          <span>Motherboard</span>
                          <strong>ASUSTeK GA401QM v1.0</strong>
                        </div>
                        <div className={styles.specsItem}>
                          <span>System Architecture</span>
                          <strong>64-bit OS, x64-based processor</strong>
                        </div>
                        <div className={styles.specsItem}>
                          <span>BIOS Version</span>
                          <strong>GA401QM.408 (Release Date: 2023-04-12)</strong>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TAB 3: BATTERY HEALTH */}
                  {activeTab === 'battery' && (
                    <div className={styles.batteryMockCard}>
                      <div className={styles.batteryMockHeader}>
                        <div className={styles.batteryMockTitle}>
                          <Zap size={20} />
                          <h3>Battery Health Intelligence</h3>
                        </div>
                        <span className={styles.proBadge} style={{ background: 'rgba(52, 211, 153, 0.1)', color: 'var(--green)' }}>Good Condition</span>
                      </div>

                      <div className={styles.batteryMockGrid}>
                        <div className={styles.batteryWearVisual}>
                          <div className={styles.batteryWearRing}>
                            <svg viewBox="0 0 100 100">
                              <circle className={styles.ringBg} cx="50" cy="50" r="45" />
                              <circle 
                                className={styles.ringFill} 
                                cx="50" cy="50" r="45" 
                                style={{
                                  stroke: 'var(--green)',
                                  strokeDashoffset: calcDashOffset(12) // 12% wear
                                }}
                              />
                            </svg>
                            <div className={styles.batteryWearValue}>
                              <strong>88%</strong>
                              <span>Health</span>
                            </div>
                          </div>
                          <p style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: '8px' }}>
                            12% capacity wear detected
                          </p>
                        </div>

                        <div className={styles.batteryMetrics}>
                          <div className={styles.batteryMetricItem}>
                            <label>Design Capacity</label>
                            <strong>76,000 mWh</strong>
                          </div>
                          <div className={styles.batteryMetricItem}>
                            <label>Full Charge Capacity</label>
                            <strong>66,880 mWh</strong>
                          </div>
                          <div className={styles.batteryMetricItem}>
                            <label>Design Cycle Count</label>
                            <strong>142 Cycles</strong>
                          </div>
                          <div className={styles.batteryMetricItem}>
                            <label>Estimated Lifespan</label>
                            <strong>3+ Years Remaining</strong>
                          </div>
                        </div>
                      </div>

                      <div className={styles.batteryRecBox}>
                        <strong>Pro Battery Tip:</strong> Your battery is healthy. Avoid keeping your laptop plugged in at 100% charge for weeks. If your manufacturer provides charge limiting software (e.g. limit to 80%), enable it to prolong life.
                      </div>
                    </div>
                  )}

                  {/* TAB 4: CPU / RAM LOAD */}
                  {activeTab === 'performance' && (
                    <div className={styles.cpuUsageCard}>
                      <div className={styles.cpuHeader}>
                        <Activity size={20} color="var(--accent)" />
                        <h3>Real-time System Load Simulation</h3>
                      </div>
                      
                      <div className={styles.usageGrid}>
                        {/* CPU */}
                        <div className={styles.usageItem}>
                          <div className={styles.usageLabelRow}>
                            <span>CPU Active Overhead</span>
                            <strong>14% Usage</strong>
                          </div>
                          <div className={styles.usageBarBg}>
                            <div className={styles.usageBarFill} style={{ width: '14%', background: 'var(--accent)' }}></div>
                          </div>
                          <p style={{ fontSize: '0.75rem', color: 'var(--text-2)', margin: 0 }}>
                            Low background demand. Main components are idle and responsive.
                          </p>
                        </div>

                        {/* RAM */}
                        <div className={styles.usageItem}>
                          <div className={styles.usageLabelRow}>
                            <span>RAM Capacity Load</span>
                            <strong>58% Load (9.3 / 16.0 GB)</strong>
                          </div>
                          <div className={styles.usageBarBg}>
                            <div className={styles.usageBarFill} style={{ width: '58%', background: 'var(--amber)' }}></div>
                          </div>
                          <p style={{ fontSize: '0.75rem', color: 'var(--text-2)', margin: 0 }}>
                            Moderate memory usage. Startup apps are occupying ~3.2 GB of background memory.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TAB 5: STARTUP PROGRAMS */}
                  {activeTab === 'startup' && (
                    <div className={styles.startupCard}>
                      <div className={styles.startupHeader}>
                        <h3>Startup Application Breakdown</h3>
                        <span className={styles.proBadge} style={{ background: 'rgba(245, 158, 11, 0.1)', color: 'var(--amber)' }}>23 Apps Total</span>
                      </div>

                      <div className={styles.startupTableWrapper}>
                        <table className={styles.startupTable}>
                          <thead>
                            <tr>
                              <th>Program Name</th>
                              <th>Registry / Shortcut Location</th>
                              <th>PCCheckr Recommendation</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Spotify Web Helper</td>
                              <td>HKCU\Software\Microsoft\Windows\CurrentVersion\Run</td>
                              <td className={styles.recDisable}>Safe to disable from startup</td>
                            </tr>
                            <tr>
                              <td>Discord Update</td>
                              <td>Startup Folder Shortcut</td>
                              <td className={styles.recDisable}>Safe to disable from startup</td>
                            </tr>
                            <tr>
                              <td>Windows Security Notification</td>
                              <td>HKLM\Software\Microsoft\Windows\CurrentVersion\Run</td>
                              <td className={styles.recKeep}>Keep enabled — security component</td>
                            </tr>
                            <tr>
                              <td>NVIDIA ShadowPlay</td>
                              <td>Scheduled Task at Login</td>
                              <td className={styles.recKeep}>Keep if gaming, otherwise safe to disable</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Demonstration of Plain English explanations */}
                  <div className={styles.englishExplanation}>
                    <div className={styles.englishIcon}>
                      <BookOpen size={24} />
                    </div>
                    <div className={styles.englishContent}>
                      <h4>Plain-English Explanations</h4>
                      <p>
                        Every technical issue detected contains a simplified explanation of <strong>why it matters</strong> to you, 
                        and clear recommendations on what to do (and what dangerous software to avoid). We explain Windows' 
                        obscure background errors in simple, non-jargon terms so you can act with confidence.
                      </p>
                    </div>
                  </div>

                </div>

              </div>

            </div>
          </section>

          {/* Sticky/Final CTA section near bottom */}
          <section className={styles.bottomCta}>
            <h2>Ready to check your PC health?</h2>
            <p>
              Get a complete, plain-English breakdown of your PC's bottleneck areas in minutes. Safe, private, and read-only.
            </p>
            <div className={styles.bottomCtaButtons}>
              <button onClick={handleStartScan} className="btn btn-primary">
                Start Free Scan <Zap size={16} />
              </button>
              <button onClick={handleUpgradePro} className="btn btn-secondary">
                Upgrade to Pro <Layers size={16} />
              </button>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
}

// Simple helper to calculate circular progress offset
function calcDashOffset(percentage: number) {
  const radius = 45;
  const circumference = 2 * Math.PI * radius; // ~282.74
  return circumference - (circumference * (100 - percentage)) / 100;
}
